import { useSyncExternalStore } from 'react'

import type { TagestrennerAngaben } from '../api/types'

/**
 * What the input line holds per place -- text and attachments that are
 * not sent yet (1.22.0).
 *
 * **Why this exists.** The chat and the draft of the report (#85) are the
 * same page element at the same place in the tree (`<ThreadPage />` and
 * `<ThreadPage entwurf />`, no key), so React keeps the `Composer` mounted
 * across the switch, and with it its state. From the field: photos
 * attached in the draft stood in the chat's line after the switch, and
 * "Senden" there sent them to everyone at once; the other way round the
 * photos landed in the draft. The same happened between two trips in the
 * chat list. The decision: **the input stays where it was written.** On
 * leaving a place the line puts its content here, on returning it takes
 * it back; nothing wanders, nothing asks.
 *
 * The key is the place: trip, partner (a one-to-one thread), draft or
 * chat. The store lives for the page's lifetime -- a reload loses it, like
 * an unsent line always did; the uploaded pictures stay on the server and
 * are swept after a day (`Speicher::verwaist()`), and the photo picker
 * calls them "noch nirgends".
 */

/** The stage of an attachment on its way to the server. */
export type Stufe = 'wartet' | 'rechnet' | 'lädt' | 'fertig' | 'offline' | 'fehler'

/**
 * A chosen photo, video or voice note on its way to the server.
 *
 * The key is its own: the id from the server comes at the end of the way,
 * the tile stands at the beginning. Keys are handed out once per page
 * (`naechsterSchluessel` in the Composer), so an upload that finishes
 * after a switch can still find its tile in the store.
 */
export interface Attachment {
  key: number
  preview: string | null
  stufe: Stufe
  /** Share of the upload, 0 to 1. */
  fortschritt: number
  /**
   * What the file weighs -- from the start. The iOS share sheet does not
   * remember the size choice, so the same shot arrives as 25 MB or 300 MB;
   * whoever learns that from the server's refusal has already waited.
   */
  bytes: number
  art: 'bild' | 'video' | 'ton'
  /** Videos only, once read. */
  dauerMs?: number
  /** The limit this file will fail at, in MB, else 0. */
  ueberGrenze?: number
  /**
   * The reduced picture. Stays until the message is out: without net it
   * goes into the outbox, and there the picture itself stands, not an id.
   */
  blob?: Blob
  /** Its thumbnail from `prepareImage()` -- goes into the outbox with it (1.3.0). */
  thumb?: Blob | null
  id?: number
  /**
   * It was in the trip already (1.52.0, "Aus dieser Reise" in the chat): a
   * picture that hangs at a point or spot. The cross on its tile takes it
   * out of the line only -- not off the server, where it belongs to the
   * point.
   */
  bestand?: boolean
  problem?: string
  /**
   * A note that is no error -- so far exactly one: the video could not be
   * reduced and goes out as it came from the camera.
   */
  hinweis?: string
  /** What became of the video: "184 MB → 27 MB · 1080p". */
  ausbeute?: string
  /**
   * A video's upload waits for the net (1.176.1): still on its way, and
   * removable, because nobody knows how long the net stays away.
   */
  waitsForNet?: boolean
}

export interface Eingabe {
  text: string
  fotos: Attachment[]
  /** The day separator chip (#113) -- it waits with the text and the tiles. */
  tagestrenner?: TagestrennerAngaben | null
}

const vorrat = new Map<string, Eingabe>()
const zuhoerer = new Set<() => void>()

function melden(): void {
  for (const z of zuhoerer) z()
}

/** The place an input line belongs to. */
export function eingabeSchluessel(tripId: number, withUser: number | undefined, entwurf: boolean): string {
  return `${tripId}|${entwurf ? 'entwurf' : (withUser ?? '')}`
}

/** Put the line's content aside for this place; nothing to keep clears the place. */
export function eingabeMerken(schluessel: string, eingabe: Eingabe): void {
  if (eingabe.text.trim() === '' && eingabe.fotos.length === 0 && !eingabe.tagestrenner) {
    vorrat.delete(schluessel)
  } else {
    vorrat.set(schluessel, eingabe)
  }
  melden()
}

/** Take the content back for this place -- it leaves the store with that. */
export function eingabeHolen(schluessel: string): Eingabe | undefined {
  const eingabe = vorrat.get(schluessel)
  if (eingabe !== undefined) {
    vorrat.delete(schluessel)
    melden()
  }
  return eingabe
}

/**
 * An upload that finished after the switch updates its tile here.
 *
 * Returns whether the tile was found anywhere in the store.
 */
export function eingabeAendern(anhangKey: number, teil: Partial<Attachment>): boolean {
  for (const [schluessel, eingabe] of vorrat) {
    if (!eingabe.fotos.some((f) => f.key === anhangKey)) continue
    vorrat.set(schluessel, {
      ...eingabe,
      fotos: eingabe.fotos.map((f) => (f.key === anhangKey ? { ...f, ...teil } : f)),
    })
    return true
  }
  return false
}

function abonnieren(z: () => void): () => void {
  zuhoerer.add(z)
  return () => {
    zuhoerer.delete(z)
  }
}

/**
 * Where something waits for this trip -- for the dot in the pill.
 *
 * The snapshot is a string so that an unchanged state keeps its identity;
 * `useSyncExternalStore` compares by identity.
 */
export function useWartend(tripId: number): { chat: boolean; entwurf: boolean } {
  const stand = useSyncExternalStore(
    abonnieren,
    () =>
      `${vorrat.has(eingabeSchluessel(tripId, undefined, false)) ? 1 : 0}${vorrat.has(eingabeSchluessel(tripId, undefined, true)) ? 1 : 0}`,
    () => '00',
  )
  return { chat: stand[0] === '1', entwurf: stand[1] === '1' }
}

